import React, { useCallback, useEffect, useState } from 'react';
import { FileCheck2, Loader2, RefreshCw, Search, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ewbApi } from '@/services/ewayBillApi';
import { fmtDT } from '@/components/ewayBill/shared';
import { NewEwbDialog } from '@/components/ewayBill/NewEwbDialog';
import { TransfersToShipCsv } from '@/components/ewayBill/TransfersToShipCsv';
import { JwTransferPrintBtn } from '@/components/jobWork/JwTransfer';

const SHOW = 8;
const TONE = { ACTIVE: 'bg-emerald-100 text-emerald-800', TALLY_ERROR: 'bg-rose-100 text-rose-700' };

const EwbChip = ({ d }) => (
  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[d.ewb_status] || 'bg-amber-100 text-amber-800'}`} data-testid={`ewb-doc-chip-${d.doc_no}`}>
    {d.ewb_status === 'ACTIVE' ? `EWB ${d.ewb_no}${d.ewb_valid_upto ? ` · till ${fmtDT(d.ewb_valid_upto)}` : ''}` : d.ewb_no}
  </span>
);

const Row = ({ d, onCreate, challan }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm" data-testid={`ewb-doc-row-${d.doc_no}`}>
    <span className="font-mono font-semibold">{d.doc_no}</span>
    <span className="truncate text-gray-700">{d.party || '—'}</span>
    {d.processing_unit && <span className="rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-800 ring-1 ring-teal-200" data-testid={`ewb-doc-unit-${d.doc_no}`}>Unit: {d.processing_unit}</span>}
    <span className="text-xs text-gray-500">{d.note}</span>
    <span className="ml-auto text-xs text-gray-500">{d.date}</span>
    {d.ewb_status ? <EwbChip d={d} />
      : <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onCreate(d)} data-testid={`ewb-doc-create-${d.doc_no}`}><FileCheck2 className="w-3.5 h-3.5 mr-1" />Create e-way bill</Button>}
    {challan && d.jo_id && <JwTransferPrintBtn kind="processing" refNo={d.doc_no} s={{ ewb_no: d.ewb_number }} label="Job Work Challan"
      className="h-7 text-xs" testId={`ewb-doc-challan-${d.doc_no}`} />}
  </div>
);

/** Internal Transfer / Job Order pages: this section's recent documents with their e-way bill — goods leave at Gate Outward only on an ACTIVE bill. */
export const EwbDocPanel = ({ docType, section, title, challan = false }) => {
  const [cfg, setCfg] = useState(undefined);
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const load = useCallback(() => ewbApi.documents({ doc_type: docType, section, q }).then(setDocs).catch(() => setDocs([])), [docType, section, q]);
  useEffect(() => { ewbApi.config().then(setCfg).catch(() => setCfg(null)); }, []);
  useEffect(() => { if (!cfg) return undefined; const t = setTimeout(load, 300); return () => clearTimeout(t); }, [cfg, load]);
  const rows = all ? docs || [] : (docs || []).slice(0, SHOW);
  return (
    <div className="rounded-xl border border-[#E5DFD0] bg-white shadow-sm" data-testid={`ewb-doc-panel-${docType}`}>
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
        <Truck className="w-5 h-5 text-[#2D5F4F]" />
        <div><div className="font-semibold text-[#1A1F2E]">{title}</div>
          <div className="text-xs text-gray-500">Goods go out at Gate Outward only once the e-way bill number is in WMS.</div></div>
        {cfg && (
          <div className="ml-auto flex items-center gap-2">
            <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number" className="h-8 w-44 pl-8 text-sm" data-testid="ewb-doc-panel-search" /></div>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={load} data-testid="ewb-doc-panel-refresh"><RefreshCw className="w-4 h-4" /></Button>
            {docType === 'branch_transfer' && <TransfersToShipCsv section={section} />}
          </div>
        )}
      </div>
      {cfg === undefined ? <Loader2 className="w-5 h-5 mx-auto my-4 animate-spin text-amber-600" />
        : cfg === null ? <p className="px-4 py-3 text-sm text-gray-500" data-testid="ewb-doc-panel-no-access">You need the E-Way Bill permission (Admin → User Management) to make e-way bills here.</p>
          : docs === null ? <Loader2 className="w-5 h-5 mx-auto my-4 animate-spin text-amber-600" />
            : docs.length === 0 ? <p className="px-4 py-3 text-sm text-gray-500" data-testid="ewb-doc-panel-empty">Nothing found</p>
              : (
                <div className="divide-y" data-testid="ewb-doc-panel-list">
                  {rows.map((d, n) => <Row key={`${d.doc_no}-${d.source?.kind}-${n}`} d={d} challan={challan} onCreate={(x) => setPrefill({ doc_type: docType, doc_no: x.doc_no, auto: true })} />)}
                  {docs.length > SHOW && <button type="button" onClick={() => setAll(!all)} className="w-full py-2 text-xs font-medium text-[#2D5F4F] hover:bg-slate-50" data-testid="ewb-doc-panel-more">{all ? 'Show less' : `Show all ${docs.length}`}</button>}
                </div>
              )}
      {cfg && <NewEwbDialog open={!!prefill} onClose={() => { setPrefill(null); load(); }} cfg={cfg} prefill={prefill} onCreated={load} />}
    </div>
  );
};

export default EwbDocPanel;
